import React, { useEffect, useState } from "react";
import axios from "axios";
import AppConst from "../apis/appconst";
import logo from "../company logo.jpeg";
import { toast } from "react-toastify";
import { useNavigate } from 'react-router-dom';
import { apiClient } from "../services/api";

// --- Modern Fixed Header ---
function MyHeader() {
  return (
    <header className="modern-header-fixed">
      <div className="container-fluid">
        <div className="header-content-fixed">
          <div className="header-left">
            <a
              href="https://websmart.org.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="header-brand"
            >
              <img src={logo} alt="Company Logo" className="header-logo" />
              <span className="header-brand-text">Web Smart Technology</span>
            </a>
          </div>
          
          <div className="header-center">
            <h1 className="smart-hospital-title">
              <i className="ri-hospital-line me-2"></i>
              Smart Hospital Management Software
            </h1>
          </div>
          
          <div className="header-center">
            <a href="tel:+919126538413" className="header-call">
              <i className="ri-phone-line"></i>
              <span className="call-number">+91 9126538413</span>
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}

// --- Modern Footer ---
function MyFooter() {
  return (
    <footer className="modern-footer">
      <div className="container-fluid">
        <div className="row align-items-center">
          <div className="col-md-6 text-center text-md-start mb-2 mb-md-0">
            <span className="footer-text">© {new Date().getFullYear()} HMS. All rights reserved.</span>
          </div>
          <div className="col-md-6 text-center text-md-end">
            <a
              href="https://websmart.org.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-link"
            >
              <img src={logo} alt="Company Logo" className="footer-logo" />
              <span>Web Smart Technology</span>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

// --- Main Modern Login Component ---
export default function AppLogin() {
  const [formData, setFormData] = useState({ 
    email: "", 
    password: "", 
    alertMsg: "", 
    alertMsgTyp: "", 
    isAlert: false 
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // License management states
  const [showRenewalWarning, setShowRenewalWarning] = useState(false);
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [licenseData, setLicenseData] = useState(null);
  const [checkingLicense, setCheckingLicense] = useState(true);
  const [isRenewalProcessing, setIsRenewalProcessing] = useState(false);
  
  // Receipt states
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState(null);

  const navigate = useNavigate();

  // Check license status on component mount
  useEffect(() => {
    checkLicenseStatus();
  }, []);

  const checkLicenseStatus = async () => {
    try {
      const res = await axios.get(`${AppConst.BASE_URL}${AppConst.CheckLicenseStatus}`);
      
      if (res.data && res.data.status) {
        const license = res.data.license;
        setLicenseData(license);

        // ✅ If license EXPIRED - show full screen modal (NO CLOSE BUTTON!)
        if (res.data.should_block) {
          setShowExpiredModal(true);
          return;
        }

        // If expiring soon (within 7 days), show warning popup (CAN CLOSE)
        if (res.data.should_warn && license.days_remaining >= 0) {
          setShowRenewalWarning(true);
        }
      }
    } catch (err) {
      console.error('Error checking license:', err);
    } finally {
      setCheckingLicense(false);
    }
  };

  const handlePayNow = async () => {
    setIsRenewalProcessing(true);
  
    try {
      const stored = sessionStorage.getItem('ldata');
      let userId = null;
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          userId = parsed?.user_id;
        } catch (e) { /* ignore */ }
      }
  
      const fd = new FormData();
      fd.append('user_id', userId || '');
  
      const orderRes = await axios.post(
        `${AppConst.BASE_URL}${AppConst.CreateRenewalOrder}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
  
      console.log('Order response:', orderRes.data); // Debug
  
      if (!orderRes.data || !orderRes.data.status) {
        toast.error(orderRes.data?.message || 'Failed to create payment order');
        setIsRenewalProcessing(false);
        return;
      }
  
      const { order } = orderRes.data;
  
      // ✅ CHECK IF RAZORPAY IS LOADED
      if (!window.Razorpay) {
        console.error('Razorpay not loaded!');
        toast.error('Payment gateway not loaded. Please refresh the page.');
        setIsRenewalProcessing(false);
        return;
      }
  
      console.log('Razorpay loaded ✅');
  
      const options = {
        key: order.key_id,
        amount: order.amount * 100,
        currency: order.currency,
        name: 'Hospital Management System',
        description: 'Software License Renewal - 1 Year',
        order_id: order.id,
        handler: async function (response) {
          console.log('Payment success:', response);
          await verifyPayment(response, userId);
        },
        prefill: {
          email: formData.email || '',
          contact: '9126538413'
        },
        notes: {
          license_renewal: 'true'
        },
        theme: {
          color: '#667eea'
        },
        modal: {
          ondismiss: function () {
            console.log('Payment cancelled by user');
            setIsRenewalProcessing(false);
            toast.info('Payment cancelled');
          }
        }
      };
  
      console.log('Creating Razorpay instance with options:', options);
  
      try {
        const rzp = new window.Razorpay(options);
        console.log('Opening Razorpay modal...');
        rzp.open();
      } catch (rzpError) {
        console.error('Razorpay initialization error:', rzpError);
        toast.error('Failed to open payment gateway: ' + rzpError.message);
        setIsRenewalProcessing(false);
      }
  
    } catch (err) {
      console.error('Payment error:', err);
      toast.error('Failed to initiate payment: ' + err.message);
      setIsRenewalProcessing(false);
    }
  };

  const verifyPayment = async (paymentResponse, userId) => {
    try {
      const fd = new FormData();
      fd.append('razorpay_payment_id', paymentResponse.razorpay_payment_id);
      fd.append('razorpay_order_id', paymentResponse.razorpay_order_id);
      fd.append('razorpay_signature', paymentResponse.razorpay_signature);
      fd.append('user_id', userId || '');

      const res = await axios.post(
        `${AppConst.BASE_URL}${AppConst.VerifyAndRenew}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (res.data && res.data.status) {
        const renewalData = res.data.renewal;
        
        // ✅ Set receipt data
        setReceiptData({
          receipt_number: renewalData.receipt_number,
          payment_id: renewalData.payment_id,
          amount: renewalData.amount,
          currency: 'INR',
          payment_date: new Date().toISOString(),
          payment_status: 'success',
          company_name: renewalData.company_name,
          license_key: renewalData.license_key,
          previous_expiry_date: renewalData.previous_expiry,
          new_expiry_date: renewalData.new_expiry,
          receipt_id: renewalData.receipt_id
        });
        
        // ✅ Show receipt modal
        setShowReceipt(true);
        setShowExpiredModal(false);
        setShowRenewalWarning(false);
        
        toast.success('🎉 License renewed successfully!', {
          autoClose: 3000
        });

      } else {
        toast.error(res.data?.message || 'Payment verification failed');
        setIsRenewalProcessing(false);
      }

    } catch (err) {
      console.error('Verification error:', err);
      toast.error('Payment verification failed');
      setIsRenewalProcessing(false);
    }
  };

  const handlePrintReceipt = () => {
    window.print();
  };

  const handleCloseReceipt = async () => {
    setShowReceipt(false);
    setReceiptData(null);
    
    // Refresh license status
    await checkLicenseStatus();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value, isAlert: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Re-check license before allowing login
    try {
      const licenseCheck = await axios.get(`${AppConst.BASE_URL}${AppConst.CheckLicenseStatus}`);
      if (licenseCheck.data && licenseCheck.data.should_block) {
        setShowExpiredModal(true);
        return;
      }
    } catch (err) {
      console.error('License check error:', err);
    }
    
    setIsLoading(true);
    
    const fd = new FormData();
    fd.append("email", formData.email);
    fd.append("password", formData.password);
  
    try {
      const response = await axios.post(`${AppConst.BASE_URL}${AppConst.loginUrl}`, fd);
      
      if (response.data && response.data.status) {
        const user = response.data.user_dt || {};
        const userName = user.name || user.username || user.fname || "User";
        const roleId = user.role_id;
      
        sessionStorage.setItem("ldata", JSON.stringify(user));
        sessionStorage.setItem("lastActivity", Date.now().toString());
      
        toast.success(`👋 Welcome ${userName}!`, {
          position: "top-right",
          autoClose: 1000,
        });
      
        let redirectPath = "/";
        
        if (roleId !== 1) {
          try {
            const moduleResponse = await axios.get(
              `${AppConst.BASE_URL}${AppConst.GetModuleAssign}`,
              { params: { role_id: roleId } }
            );
            
            if (moduleResponse.data.status && moduleResponse.data.list.length > 0) {
              const firstModuleId = moduleResponse.data.list[0];
              
              const allModulesResponse = await axios.get(
                `${AppConst.BASE_URL}${AppConst.GetAllModules}`
              );
              
              if (allModulesResponse.data.status) {
                const firstModule = allModulesResponse.data.list.find(
                  m => m.id === firstModuleId
                );
                
                if (firstModule && firstModule.slug) {
                  redirectPath = `/${firstModule.slug}`;
                }
              }
            }
          } catch (err) {
            console.error("Error fetching modules:", err);
          }
        }
        
        navigate(redirectPath, { replace: true });
        
      } else {
        setIsLoading(false);
        setFormData(prev => ({
          ...prev,
          isAlert: true,
          alertMsg: response.data?.success || 'Invalid email or password. Please try again.',
          alertMsgTyp: response.data?.type || 'danger'
        }));
      }
    } catch (error) {
      setIsLoading(false);
      console.error("LOGIN ERROR:", error?.response?.data || error);
      setFormData((p) => ({ 
        ...p, 
        isAlert: true, 
        alertMsg: error?.response?.data?.success || "Unable to connect to server. Please try again.", 
        alertMsgTyp: "danger" 
      }));
    }
  };

  if (checkingLicense) {
    return (
      <div className="login-wrapper">
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '100vh' 
        }}>
          <div className="spinner-border text-light" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-wrapper">
      <MyHeader />

      {/* Animated Background */}
      <div className="animated-bg">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
        <div className="shape shape-4"></div>
        <div className="shape shape-5"></div>
        <div className="shape shape-6"></div>
        <div className="shape shape-7"></div>
        <div className="shape shape-8"></div>
        <div className="shape shape-9"></div>
        <div className="shape shape-10"></div>
      </div>

      {/* ✅ EXPIRED MODAL - FULL SCREEN, NO CLOSE BUTTON */}
      {showExpiredModal && licenseData && !showReceipt && (
        <div className="expired-fullscreen-modal">
          <div className="expired-modal-content">
            <div className="expired-header">
              <img src={logo} alt="Company Logo" className="expired-logo" />
              <h1 className="expired-title">Software License Expired</h1>
            </div>

            <div className="expired-icon-wrapper">
              <div className="expired-icon">
                <i className="ri-time-line"></i>
              </div>
            </div>

            <div className="expired-message">
              <h2>Kindly renew the software to continue</h2>
              <div className="expiry-info">
                <p>
                  <i className="ri-calendar-line"></i>
                  Expired on: <strong>{new Date(licenseData.expiry_date).toLocaleDateString('en-IN')}</strong>
                </p>
                <p>
                  <i className="ri-information-line"></i>
                  License Key: <strong>{licenseData.license_key}</strong>
                </p>
              </div>
            </div>

            <div className="renewal-amount">
              <div className="amount-label">Annual Renewal Fee</div>
              <div className="amount-value">
                ₹{licenseData.renewal_amount.toLocaleString('en-IN')}
                <span className="amount-period">/year</span>
              </div>
            </div>

            <div className="expired-actions">
              <button
                className="btn-pay-now-large"
                onClick={handlePayNow}
                disabled={isRenewalProcessing}
              >
                {isRenewalProcessing ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="ri-bank-card-line me-2"></i>
                    Pay Now to Renew
                  </>
                )}
              </button>

              <div className="contact-info-large">
                <p>Need assistance?</p>
                <a href="tel:+919126538413" className="contact-link-large">
                  <i className="ri-phone-line"></i>
                  +91 9126538413
                </a>
              </div>
            </div>

            <div className="renewal-features-large">
              <h3>What you get with renewal:</h3>
              <ul>
                <li><i className="ri-check-line"></i> 1 Year Software Access</li>
                <li><i className="ri-check-line"></i> New License Key</li>
                <li><i className="ri-check-line"></i> Free Updates & Bug Fixes</li>
                <li><i className="ri-check-line"></i> Priority Support</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* ✅ RECEIPT MODAL - FULL SCREEN */}
      {showReceipt && receiptData && (
        <div className="receipt-fullscreen-modal">
          <div className="receipt-modal-content print-area">
            <div className="receipt-header">
              <img src={logo} alt="Company Logo" className="receipt-logo" />
              <h1 className="receipt-title">Payment Receipt</h1>
              <div className="success-badge">
                <i className="ri-checkbox-circle-fill"></i>
                Payment Successful
              </div>
            </div>

            <div className="receipt-content">
              <div className="receipt-section">
                <h3>Receipt Details</h3>
                <div className="receipt-row">
                  <span className="label">Receipt Number:</span>
                  <span className="value">{receiptData.receipt_number}</span>
                </div>
                <div className="receipt-row">
                  <span className="label">Payment ID:</span>
                  <span className="value">{receiptData.payment_id}</span>
                </div>
                <div className="receipt-row">
                  <span className="label">Payment Date:</span>
                  <span className="value">
                    {new Date(receiptData.payment_date).toLocaleString('en-IN')}
                  </span>
                </div>
                <div className="receipt-row">
                  <span className="label">Status:</span>
                  <span className="value status-success">
                    <i className="ri-checkbox-circle-line"></i> {receiptData.payment_status}
                  </span>
                </div>
              </div>

              <div className="receipt-section">
                <h3>License Information</h3>
                <div className="receipt-row">
                  <span className="label">Company Name:</span>
                  <span className="value">{receiptData.company_name}</span>
                </div>
                <div className="receipt-row highlight">
                  <span className="label">New License Key:</span>
                  <span className="value license-key">{receiptData.license_key}</span>
                </div>
                <div className="receipt-row">
                  <span className="label">Previous Expiry:</span>
                  <span className="value">
                    {new Date(receiptData.previous_expiry_date).toLocaleDateString('en-IN')}
                  </span>
                </div>
                <div className="receipt-row highlight">
                  <span className="label">New Expiry Date:</span>
                  <span className="value new-expiry">
                    {new Date(receiptData.new_expiry_date).toLocaleDateString('en-IN')}
                  </span>
                </div>
              </div>

              <div className="receipt-section">
                <h3>Payment Summary</h3>
                <div className="receipt-row">
                  <span className="label">Renewal Period:</span>
                  <span className="value">1 Year</span>
                </div>
                <div className="receipt-row total-row">
                  <span className="label">Total Amount Paid:</span>
                  <span className="value amount">
                    ₹{receiptData.amount?.toLocaleString('en-IN')}
                  </span>
                </div>
              </div>

              <div className="receipt-footer">
                <p className="thank-you">Thank you for your payment!</p>
                <p className="footer-note">
                  This is a computer-generated receipt. No signature required.
                </p>
              </div>
            </div>

            <div className="receipt-actions no-print">
              <button className="btn-print" onClick={handlePrintReceipt}>
                <i className="ri-printer-line"></i>
                Print Receipt
              </button>
              <button className="btn-continue" onClick={handleCloseReceipt}>
                <i className="ri-arrow-right-line"></i>
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Renewal Warning Modal (Expiring Soon - CAN CLOSE) */}
      {showRenewalWarning && licenseData && !showExpiredModal && !showReceipt && (
        <>
          <div 
            className="modal-backdrop fade show" 
            style={{ zIndex: 2000 }}
            onClick={() => setShowRenewalWarning(false)}
          ></div>

          <div 
            className="modal fade show" 
            style={{ display: 'block', zIndex: 2001 }}
            tabIndex="-1"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content renewal-warning-modal">
                <div className="modal-header border-0">
                  <h5 className="modal-title">
                    <i className="ri-alarm-warning-line text-warning me-2"></i>
                    License Expiring Soon!
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowRenewalWarning(false)}
                  ></button>
                </div>

                <div className="modal-body">
                  <div className="warning-content">
                    <div className="warning-icon">
                      <i className="ri-calendar-close-line"></i>
                    </div>
                    
                    <div className="warning-message">
                      <h4>
                        {licenseData.days_remaining === 1 
                          ? '1 day left' 
                          : `${licenseData.days_remaining} days left`}
                      </h4>
                      <p>
                        Your software license will expire on{' '}
                        <strong>{new Date(licenseData.expiry_date).toLocaleDateString('en-IN')}</strong>
                      </p>
                      <p className="mb-0">
                        Renew now to continue using the software without interruption.
                      </p>
                    </div>

                    <div className="renewal-price">
                      <div className="price-label">Annual Renewal</div>
                      <div className="price-amount">
                        ₹{licenseData.renewal_amount.toLocaleString('en-IN')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-footer border-0">
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={() => setShowRenewalWarning(false)}
                  >
                    Later
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handlePayNow}
                    disabled={isRenewalProcessing}
                  >
                    {isRenewalProcessing ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Processing...
                      </>
                    ) : (
                      <>
                        <i className="ri-bank-card-line me-2"></i>
                        Pay Now
                      </>
                    )}
                  </button>
                </div>

                <div className="modal-footer-info">
                  <p>
                    <i className="ri-phone-line me-1"></i>
                    Need help? Call <a href="tel:+919126538413">+91 9126538413</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <main className="login-main">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-10 col-lg-8 col-xl-6" style={{ marginTop: "-80px" }}>
              <div className="login-container">
                <div className="welcome-hand-wrapper" style={{ marginTop: "-20px" }}>
                  <div className="welcome-hand">
                    👋
                  </div>
                  <div className="welcome-text">
                    Hi
                  </div>
                </div>

                <div className="login-header">
                  <h1 className="login-title">Welcome Back</h1>
                  <p className="login-subtitle">Sign in to continue to your dashboard</p>
                </div>

                {formData.isAlert && (
                  <div className={`modern-alert ${formData.alertMsgTyp === 'success' ? 'alert-success' : 'alert-danger'}`}>
                    <div className="alert-icon">
                      {formData.alertMsgTyp === 'success' ? (
                        <i className="ri-checkbox-circle-line"></i>
                      ) : (
                        <i className="ri-error-warning-line"></i>
                      )}
                    </div>
                    <div className="alert-content">
                      <div className="alert-title">
                        {formData.alertMsgTyp === 'success' ? 'Success!' : 'Login Failed'}
                      </div>
                      <div className="alert-message">{formData.alertMsg}</div>
                    </div>
                    <button 
                      className="alert-close" 
                      onClick={() => setFormData(p => ({...p, isAlert: false}))}
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="login-form">
                  <div className="form-group">
                    <label htmlFor="loginEmail" className="form-label">
                      <i className="ri-mail-line"></i> Email Address
                    </label>
                    <input
                      id="loginEmail"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="form-control modern-input"
                      placeholder="Enter your email"
                      required
                      disabled={isLoading}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="loginPassword" className="form-label">
                      <i className="ri-lock-line"></i> Password
                    </label>
                    <div className="password-input-wrapper">
                      <input
                        id="loginPassword"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={handleChange}
                        className="form-control modern-input"
                        placeholder="Enter your password"
                        required
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPassword((s) => !s)}
                        disabled={isLoading}
                      >
                        <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                      </button>
                    </div>
                  </div>

                  <button 
                    className="btn-login" 
                    type="submit"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Signing In...
                      </>
                    ) : (
                      <>
                        <span>Sign In</span>
                        <i className="ri-arrow-right-line"></i>
                      </>
                    )}
                  </button>
                </form>

                <div className="login-footer">
                  <p className="security-text">
                    <i className="ri-shield-check-line"></i>
                    Your connection is secure and encrypted
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <MyFooter />

      <style>{`
        /* === ROOT VARIABLES === */
        :root {
          --primary-color: #4f46e5;
          --primary-dark: #4338ca;
          --primary-light: #6366f1;
          --success-color: #10b981;
          --success-light: #d1fae5;
          --danger-color: #ef4444;
          --danger-light: #fee2e2;
          --text-primary: #1f2937;
          --text-secondary: #6b7280;
          --border-color: #e5e7eb;
        }

        /* ✅ EXPIRED FULLSCREEN MODAL */
        .expired-fullscreen-modal {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          overflow-y: auto;
        }

        .expired-modal-content {
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(20px);
          border-radius: 24px;
          padding: 2rem;
          max-width: 700px;
          width: 100%;
          max-height: 95vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1);
          margin: auto;
        }

        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .expired-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .expired-logo {
          height: 60px;
          margin-bottom: 1rem;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .expired-title {
          font-size: 1.75rem;
          font-weight: 700;
          color: #dc2626;
          margin: 0;
        }

        .expired-icon-wrapper {
          display: flex;
          justify-content: center;
          margin-bottom: 2rem;
        }

        .expired-icon {
          width: 100px;
          height: 100px;
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 3rem;
          color: white;
          box-shadow: 0 10px 30px rgba(239, 68, 68, 0.4);
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        .expired-message {
          text-align: center;
          margin-bottom: 2rem;
        }

        .expired-message h2 {
          font-size: 1.5rem;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 1rem;
        }

        .expiry-info {
          background: #fef3c7;
          border: 1px solid #fbbf24;
          border-radius: 12px;
          padding: 1rem;
          margin-top: 1rem;
        }

        .expiry-info p {
          margin: 0.5rem 0;
          font-size: 0.95rem;
          color: #92400e;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .expiry-info i {
          color: #f59e0b;
        }

        .renewal-amount {
          background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
          border: 2px solid #3b82f6;
          border-radius: 16px;
          padding: 1.5rem;
          text-align: center;
          margin-bottom: 2rem;
        }

        .amount-label {
          font-size: 0.875rem;
          font-weight: 600;
          color: #1e40af;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 0.5rem;
        }

        .amount-value {
          font-size: 2.5rem;
          font-weight: 700;
          color: #1e3a8a;
        }

        .amount-period {
          font-size: 1rem;
          font-weight: 400;
          color: #3b82f6;
        }

        .expired-actions {
          margin-bottom: 2rem;
        }

        .btn-pay-now-large {
          width: 100%;
          padding: 1.25rem;
          font-size: 1.125rem;
          font-weight: 600;
          color: white;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border: none;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 14px rgba(16, 185, 129, 0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .btn-pay-now-large:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(16, 185, 129, 0.5);
        }

        .btn-pay-now-large:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .contact-info-large {
          text-align: center;
          margin-top: 1.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid #e5e7eb;
        }

        .contact-info-large p {
          font-size: 0.875rem;
          color: #6b7280;
          margin-bottom: 0.5rem;
        }

        .contact-link-large {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 1.125rem;
          font-weight: 600;
          color: #3b82f6;
          text-decoration: none;
          transition: color 0.2s;
        }

        .contact-link-large:hover {
          color: #2563eb;
        }

        .contact-link-large i {
          font-size: 1.25rem;
        }

        .renewal-features-large {
          background: #f9fafb;
          border-radius: 12px;
          padding: 1.5rem;
        }

        .renewal-features-large h3 {
          font-size: 1rem;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 1rem;
        }

        .renewal-features-large ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .renewal-features-large li {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.5rem 0;
          font-size: 0.95rem;
          color: #4b5563;
        }

        .renewal-features-large li i {
          color: #10b981;
          font-size: 1.25rem;
          flex-shrink: 0;
        }

        /* ✅ RECEIPT FULLSCREEN MODAL */
        .receipt-fullscreen-modal {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          overflow-y: auto;
        }

        .receipt-modal-content {
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(20px);
          border-radius: 24px;
          padding: 2rem;
          max-width: 700px;
          width: 100%;
          max-height: 95vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1);
          margin: auto;
        }

        .receipt-header {
          text-align: center;
          margin-bottom: 2rem;
          padding-bottom: 1.5rem;
          border-bottom: 2px solid #e5e7eb;
        }

        .receipt-logo {
          height: 60px;
          margin-bottom: 1rem;
          border-radius: 12px;
        }

        .receipt-title {
          font-size: 1.75rem;
          font-weight: 700;
          color: #1f2937;
          margin: 0.5rem 0;
        }

        .success-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          padding: 0.5rem 1.5rem;
          border-radius: 50px;
          font-weight: 600;
          font-size: 0.95rem;
          margin-top: 1rem;
        }

        .success-badge i {
          font-size: 1.25rem;
        }

        .receipt-content {
          margin-bottom: 2rem;
        }

        .receipt-section {
          margin-bottom: 2rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid #f3f4f6;
        }

        .receipt-section:last-child {
          border-bottom: none;
        }

        .receipt-section h3 {
          font-size: 1.125rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 1rem;
        }

        .receipt-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 0;
          border-bottom: 1px solid #f9fafb;
        }

        .receipt-row:last-child {
          border-bottom: none;
        }

        .receipt-row.highlight {
          background: #fef3c7;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          border-bottom: none;
          margin: 0.5rem 0;
        }

        .receipt-row .label {
          font-size: 0.95rem;
          color: #6b7280;
          font-weight: 500;
        }

        .receipt-row .value {
          font-size: 0.95rem;
          color: #1f2937;
          font-weight: 600;
          text-align: right;
        }

        .receipt-row .license-key {
          font-family: 'Courier New', monospace;
          background: #fbbf24;
          padding: 0.25rem 0.75rem;
          border-radius: 6px;
          color: #92400e;
        }

        .receipt-row .new-expiry {
          color: #059669;
          font-size: 1.05rem;
        }

        .receipt-row .status-success {
          color: #059669;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .receipt-row.total-row {
          background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
          padding: 1rem 1.5rem;
          border-radius: 12px;
          margin-top: 1rem;
        }

        .receipt-row.total-row .label {
          font-size: 1.1rem;
          color: #1e40af;
          font-weight: 700;
        }

        .receipt-row.total-row .value {
          font-size: 1.5rem;
          color: #1e3a8a;
          font-weight: 700;
        }

        .receipt-footer {
          text-align: center;
          margin-top: 2rem;
          padding-top: 1.5rem;
          border-top: 2px solid #e5e7eb;
        }

        .thank-you {
          font-size: 1.125rem;
          font-weight: 600;
          color: #059669;
          margin-bottom: 0.5rem;
        }

        .footer-note {
          font-size: 0.875rem;
          color: #9ca3af;
          margin: 0;
        }

        .receipt-actions {
          display: flex;
          gap: 1rem;
          margin-top: 2rem;
        }

        .btn-print, .btn-continue {
          flex: 1;
          padding: 1rem;
          font-size: 1rem;
          font-weight: 600;
          border: none;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .btn-print {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
          box-shadow: 0 4px 14px rgba(59, 130, 246, 0.4);
        }

        .btn-print:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(59, 130, 246, 0.5);
        }

        .btn-continue {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          box-shadow: 0 4px 14px rgba(16, 185, 129, 0.4);
        }

        .btn-continue:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(16, 185, 129, 0.5);
        }

        /* PRINT STYLES */
        @media print {
          .receipt-fullscreen-modal {
            background: white;
            padding: 0;
          }

          .receipt-modal-content {
            box-shadow: none;
            border-radius: 0;
            max-width: 100%;
          }

          .no-print {
            display: none !important;
          }

          .receipt-row .license-key {
            background: #ffedd5 !important;
            color: #000 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .receipt-row.total-row {
            background: #f0f9ff !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .success-badge {
            background: #10b981 !important;
            color: white !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }

        /* RENEWAL WARNING MODAL (CAN CLOSE) */
        .renewal-warning-modal {
          border-radius: 20px;
          overflow: hidden;
        }

        .renewal-warning-modal .modal-header {
          background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
          padding: 1.5rem;
        }

        .renewal-warning-modal .modal-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #92400e;
          display: flex;
          align-items: center;
        }

        .warning-content {
          text-align: center;
          padding: 1rem 0;
        }

        .warning-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          font-size: 2.5rem;
          color: white;
          box-shadow: 0 8px 20px rgba(251, 191, 36, 0.4);
        }

        .warning-message h4 {
          font-size: 1.75rem;
          font-weight: 700;
          color: #dc2626;
          margin-bottom: 0.75rem;
        }

        .warning-message p {
          font-size: 1rem;
          color: #4b5563;
          margin-bottom: 0.5rem;
        }

        .renewal-price {
          background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
          border-radius: 12px;
          padding: 1.25rem;
          margin-top: 1.5rem;
        }

        .price-label {
          font-size: 0.875rem;
          font-weight: 600;
          color: #1e40af;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 0.5rem;
        }

        .price-amount {
          font-size: 2rem;
          font-weight: 700;
          color: #1e3a8a;
        }

        .modal-footer-info {
          background: #f9fafb;
          padding: 1rem;
          text-align: center;
          border-top: 1px solid #e5e7eb;
        }

        .modal-footer-info p {
          margin: 0;
          font-size: 0.875rem;
          color: #6b7280;
        }

        .modal-footer-info a {
          color: #3b82f6;
          font-weight: 600;
          text-decoration: none;
        }

        .modal-footer-info a:hover {
          color: #2563eb;
        }

        /* LOGIN PAGE STYLES */
        .login-wrapper {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          position: relative;
          overflow: hidden;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding-top: 70px;
        }

        .animated-bg {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 0;
          overflow: hidden;
          pointer-events: none;
        }

        .shape {
          position: absolute;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.25);
          backdrop-filter: blur(10px);
          animation: float 8s infinite ease-in-out;
        }

        .shape-1 { width: 320px; height: 320px; top: 8%; left: 5%; animation-delay: 0s; animation-duration: 7s; }
        .shape-2 { width: 200px; height: 200px; top: 45%; left: 8%; animation-delay: 1.5s; animation-duration: 6s; }
        .shape-3 { width: 280px; height: 280px; bottom: 15%; left: 10%; animation-delay: 3s; animation-duration: 8s; }
        .shape-4 { width: 150px; height: 150px; top: 25%; left: 15%; animation-delay: 0.5s; animation-duration: 7.5s; }
        .shape-5 { width: 350px; height: 350px; top: 10%; right: 5%; animation-delay: 2s; animation-duration: 6.5s; }
        .shape-6 { width: 240px; height: 240px; top: 40%; right: 8%; animation-delay: 1s; animation-duration: 7s; }
        .shape-7 { width: 190px; height: 190px; bottom: 20%; right: 10%; animation-delay: 4s; animation-duration: 9s; }
        .shape-8 { width: 280px; height: 280px; top: 60%; right: 12%; animation-delay: 2.5s; animation-duration: 6.8s; }
        .shape-9 { width: 160px; height: 160px; top: 20%; right: 25%; animation-delay: 3.5s; animation-duration: 8.5s; }
        .shape-10 { width: 220px; height: 220px; bottom: 10%; left: 50%; animation-delay: 5s; animation-duration: 7.2s; }

        @keyframes float {
          0%, 100% { transform: translate(0, 0) rotate(0deg) scale(1); opacity: 0.25; }
          25% { transform: translate(40px, -40px) rotate(10deg) scale(1.08); opacity: 0.35; }
          50% { transform: translate(-40px, 40px) rotate(-10deg) scale(0.92); opacity: 0.2; }
          75% { transform: translate(40px, 40px) rotate(10deg) scale(1.08); opacity: 0.35; }
        }

        .modern-header-fixed {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          z-index: 1000;
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(20px);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          border-bottom: 1px solid rgba(255, 255, 255, 0.5);
        }

        .header-content-fixed {
          padding: 1rem 2rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
        }

        .header-left { flex: 0 0 auto; }

        .header-brand {
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
          text-decoration: none;
          color: var(--text-primary);
          transition: transform 0.2s;
        }

        .header-brand:hover { transform: scale(1.02); }

        .header-logo {
          height: 40px;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .header-brand-text {
          font-weight: 700;
          font-size: 1rem;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .header-center {
          flex: 1 1 auto;
          text-align: center;
        }

        .smart-hospital-title {
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .smart-hospital-title i {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .header-call {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.625rem 1.25rem;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          text-decoration: none;
          border-radius: 50px;
          font-weight: 600;
          font-size: 0.875rem;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        }

        .header-call:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(16, 185, 129, 0.4);
          color: white;
        }

        .header-call i {
          font-size: 1.125rem;
          animation: ring 2s infinite;
        }

        @keyframes ring {
          0%, 100% { transform: rotate(0deg); }
          10%, 30% { transform: rotate(-10deg); }
          20%, 40% { transform: rotate(10deg); }
          50% { transform: rotate(0deg); }
        }

        .call-number {
          font-weight: 700;
          letter-spacing: 0.5px;
        }

        .login-main {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 3rem 1rem;
          position: relative;
          z-index: 10;
        }

        .login-container {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 24px;
          padding: 3rem;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1);
          border: 1px solid rgba(255, 255, 255, 0.5);
        }

        .welcome-hand-wrapper {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .welcome-hand {
          font-size: 4rem;
          animation: wave 1s ease-in-out infinite, scaleIn 0.6s cubic-bezier(0.16, 1, 0.3, 1);
          transform-origin: 70% 70%;
          display: inline-block;
        }

        .welcome-text {
          font-size: 3rem;
          font-weight: 700;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: slideInRight 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.3s backwards;
        }

        @keyframes wave {
          0%, 100% { transform: rotate(0deg); }
          10% { transform: rotate(14deg); }
          20% { transform: rotate(-8deg); }
          30% { transform: rotate(14deg); }
          40% { transform: rotate(-4deg); }
          50% { transform: rotate(10deg); }
          60% { transform: rotate(0deg); }
        }

        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.3); }
          to { opacity: 1; transform: scale(1); }
        }

        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(-30px); }
          to { opacity: 1; transform: translateX(0); }
        }

        .login-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .login-title {
          font-size: 2rem;
          font-weight: 700;
          color: var(--text-primary);
          margin-bottom: 0.5rem;
        }

        .login-subtitle {
          font-size: 1rem;
          color: var(--text-secondary);
          margin: 0;
        }

        .modern-alert {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          padding: 1rem 1.25rem;
          border-radius: 12px;
          margin-bottom: 1.5rem;
          animation: alertSlide 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          position: relative;
        }

        @keyframes alertSlide {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .modern-alert.alert-success {
          background: var(--success-light);
          border: 1px solid var(--success-color);
        }

        .modern-alert.alert-danger {
          background: var(--danger-light);
          border: 1px solid var(--danger-color);
        }

        .alert-icon {
          font-size: 1.5rem;
          flex-shrink: 0;
        }

        .alert-success .alert-icon { color: var(--success-color); }
        .alert-danger .alert-icon { color: var(--danger-color); }

        .alert-content { flex: 1; }

        .alert-title {
          font-weight: 600;
          font-size: 0.875rem;
          margin-bottom: 0.25rem;
        }

        .alert-success .alert-title { color: #065f46; }
        .alert-danger .alert-title { color: #991b1b; }

        .alert-message { font-size: 0.875rem; line-height: 1.5; }

        .alert-success .alert-message { color: #047857; }
        .alert-danger .alert-message { color: #dc2626; }

        .alert-close {
          background: none;
          border: none;
          font-size: 1.25rem;
          cursor: pointer;
          color: inherit;
          opacity: 0.6;
          transition: opacity 0.2s;
          padding: 0;
          flex-shrink: 0;
        }

        .alert-close:hover { opacity: 1; }

        .login-form { margin-bottom: 1.5rem; }
        .form-group { margin-bottom: 1.5rem; }

        .form-label {
          display: block;
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 0.5rem;
        }

        .form-label i {
          margin-right: 0.5rem;
          color: var(--primary-color);
        }

        .modern-input {
          width: 100%;
          padding: 0.875rem 1rem;
          font-size: 1rem;
          border: 2px solid var(--border-color);
          border-radius: 12px;
          transition: all 0.3s ease;
          background: white;
        }

        .modern-input:focus {
          outline: none;
          border-color: var(--primary-color);
          box-shadow: 0 0 0 4px rgba(79, 70, 229, 0.1);
        }

        .modern-input:disabled {
          background: #f9fafb;
          cursor: not-allowed;
        }

        .password-input-wrapper { position: relative; }

        .password-toggle {
          position: absolute;
          right: 1rem;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          font-size: 1.25rem;
          padding: 0.5rem;
          transition: color 0.2s;
        }

        .password-toggle:hover { color: var(--primary-color); }
        .password-toggle:disabled { cursor: not-allowed; opacity: 0.5; }

        .btn-login {
          width: 100%;
          padding: 1rem;
          font-size: 1rem;
          font-weight: 600;
          color: white;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          box-shadow: 0 4px 14px rgba(102, 126, 234, 0.4);
        }

        .btn-login:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5);
        }

        .btn-login:active:not(:disabled) { transform: translateY(0); }
        .btn-login:disabled { opacity: 0.7; cursor: not-allowed; }

        .login-footer {
          text-align: center;
          margin-top: 2rem;
          padding-top: 2rem;
          border-top: 1px solid var(--border-color);
        }

        .security-text {
          font-size: 0.875rem;
          color: var(--text-secondary);
          margin: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .security-text i { color: var(--success-color); }

        .modern-footer {
          position: relative;
          z-index: 10;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          padding: 1.5rem 0;
          box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.1);
        }

        .footer-text {
          font-size: 0.875rem;
          color: var(--text-secondary);
        }

        .footer-link {
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
          text-decoration: none;
          color: var(--text-primary);
          font-weight: 600;
          font-size: 0.875rem;
          transition: color 0.2s;
        }

        .footer-link:hover { color: var(--primary-color); }

        .footer-logo {
          height: 32px;
          border-radius: 6px;
        }

        @media (max-width: 768px) {
          .login-wrapper { padding-top: 60px; }
          .header-content-fixed { flex-direction: column; gap: 0.75rem; padding: 0.75rem 1rem; }
          .header-left, .header-center, .header-right { flex: 0 0 auto; }
          .smart-hospital-title { font-size: 1.1rem; }
          .header-brand-text { font-size: 0.875rem; }
          .header-logo { height: 32px; }
          .header-call { padding: 0.5rem 1rem; font-size: 0.8rem; }
          .login-container { padding: 2rem 1.5rem; }
          .login-title { font-size: 1.5rem; }
          .shape { opacity: 0.7; }
          .shape-4, .shape-7, .shape-9, .shape-10 { display: none; }
          .expired-modal-content, .receipt-modal-content { 
            padding: 1.5rem 1rem; 
            max-height: 90vh;
          }
          .receipt-actions { flex-direction: column; }
          .receipt-row { flex-direction: column; align-items: flex-start; gap: 0.25rem; }
          .receipt-row .value { text-align: left; }
          .expired-header { margin-bottom: 1rem; }
          .expired-logo { height: 50px; }
          .expired-title { font-size: 1.5rem; }
          .expired-icon { width: 80px; height: 80px; font-size: 2.5rem; }
          .amount-value { font-size: 2rem; }
        }

        @media (max-width: 576px) {
          .login-wrapper { padding-top: 140px; }
          .header-content-fixed { padding: 0.5rem; }
          .smart-hospital-title { font-size: 1rem; }
          .header-brand-text { display: none; }
          .header-logo { height: 28px; }
          .call-number { font-size: 0.75rem; }
          .login-main { padding: 2rem 1rem; }
          .login-container { padding: 1.5rem 1rem; }
          .welcome-hand { font-size: 3rem; }
          .welcome-text { font-size: 2.5rem; }
          .shape { opacity: 0.5; }
          .shape-2, .shape-4, .shape-6, .shape-7, .shape-8, .shape-9, .shape-10 { display: none; }
          .expired-modal-content, .receipt-modal-content { 
            padding: 1rem 0.75rem;
            max-height: 85vh;
          }
          .expired-fullscreen-modal, .receipt-fullscreen-modal {
            padding: 0.5rem;
          }
          .expired-icon { width: 70px; height: 70px; font-size: 2rem; }
          .expired-title { font-size: 1.25rem; }
          .expired-message h2 { font-size: 1.25rem; }
          .amount-value { font-size: 1.75rem; }
          .renewal-features-large { padding: 1rem; }
        }
      `}</style>
    </div>
  );
}